import React from 'react';
import { motion } from 'framer-motion';
import { EnvelopeIcon, PhoneIcon, MapPinIcon, HandRaisedIcon } from '@heroicons/react/24/outline';
import { FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';
import { Handshake } from 'lucide-react';
import aboutData from '../Data/aboutData';

const Contact = () => {
  const { profile } = aboutData;

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }
  };

  const stagger = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  // Contact items from profile
  const contactItems = [
    {
      icon: EnvelopeIcon,
      label: "Email",
      value: profile?.email || "ahmedbouuba@gmail.com",
      href: `mailto:${profile?.email || "ahmedbouuba@gmail.com"}`
    },
    {
      icon: PhoneIcon,
      label: "Phone",
      value: profile?.phone || "+212 628 987 577",
      href: `tel:${profile?.phone || "+212628987577"}`
    },
    {
      icon: MapPinIcon,
      label: "Location",
      value: profile?.location || "Khemisset, Morocco"
    }
  ];

  // Social links – map from profile.social or use defaults
  const socialLinks = profile?.social ? profile.social.map(s => {
    let icon;
    switch (s.icon.toLowerCase()) {
      case 'github': icon = <FaGithub className="w-5 h-5" />; break;
      case 'linkedin': icon = <FaLinkedin className="w-5 h-5" />; break;
      case 'twitter': icon = <FaTwitter className="w-5 h-5" />; break;
      default: icon = null;
    }
    return { ...s, icon };
  }) : [
    { name: 'GitHub', icon: <FaGithub className="w-5 h-5" />, url: 'https://github.com/BoubaAhmed' },
    { name: 'LinkedIn', icon: <FaLinkedin className="w-5 h-5" />, url: 'https://www.linkedin.com/in/ahmed-bouba-861b91231/' },
    { name: 'Twitter', icon: <FaTwitter className="w-5 h-5" />, url: 'https://twitter.com/boubaahmed' }
  ];

  return (
    <section id="contact" className="py-20 bg-white dark:bg-black">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Section header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <h2 className="text-3xl text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
            <Handshake className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
            Let's Connect
          </h2>
          <div className="w-16 h-px bg-gray-300 dark:bg-gray-700 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 max-w-5xl mx-auto text-base font-light leading-relaxed mt-2">
            I'm always open to discussing new projects, creative ideas, or opportunities.
          </p>
        </motion.div>

        {/* Contact info grid */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6"
        >
          {/* Left column – contact details */}
          <motion.div variants={fadeInUp} className="space-y-4">
            {contactItems.map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
                <div className="flex-shrink-0 w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                  <item.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {item.label}
                  </h3>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-gray-800 dark:text-gray-200 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors text-sm"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-gray-800 dark:text-gray-200 text-sm">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>

          {/* Right column – social links & availability */}
          <motion.div variants={fadeInUp} className="space-y-4">
            {/* Social links card */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <HandRaisedIcon className="w-4 h-4" />
                Social
              </h3>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all"
                    aria-label={social.name}
                  >
                    {social.icon}
                    <span className="text-sm">{social.name}</span>
                  </a>
                ))}
              </div>
            </div>

            {/* Availability card */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800">
              <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                Availability
              </h3>
              <p className="text-sm text-gray-800 dark:text-gray-200">
                Open to freelance, internships, and full‑time roles.
              </p>
              <div className="mt-3 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Currently available
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;